<template>
  <v-container fluid class="pa-2 ma-0">
    <!-- Loading State -->
    <v-row v-if="loading" dense class="ma-0">
      <v-col
        v-for="item in skeletonCount"
        :key="'skeleton-' + item"
        cols="12"
        :sm="skeletonCols"
        :md="skeletonCols"
        :lg="skeletonCols"
        class="pa-1"
      >
        <v-card height="180" class="rounded-lg">
          <v-skeleton-loader
            type="list-item-avatar-three-line, actions"
            class="mx-auto"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- Clients List -->
    <v-row v-else dense class="ma-0">
      <v-col
        v-for="client in clients"
        :key="client.id"
        cols="12"
        :sm="gridCols"
        :md="gridCols"
        :lg="gridCols"
        class="pa-1"
      >
        <Client :client="client" />
      </v-col>

      <!-- Infinite Loading Indicator -->
      <template v-if="busyGettingData">
        <v-col
          v-for="item in skeletonCount"
          :key="'skeleton-' + item"
          cols="12"
          :sm="skeletonCols"
          :md="skeletonCols"
          :lg="skeletonCols"
          class="pa-1"
        >
          <v-card height="180" class="rounded-lg">
            <v-skeleton-loader
              type="list-item-avatar-three-line, actions"
              class="mx-auto"
            />
          </v-card>
        </v-col>
      </template>
    </v-row>

    <!-- Empty State -->
    <v-row
      v-if="!loading && clients.length <= 0"
      class="fill-height ma-0"
      align="center"
      justify="center"
    >
      <v-col cols="12" class="text-center py-8">
        <v-icon size="64" color="grey lighten-1" class="mb-4"
          >mdi-account-multiple</v-icon
        >
        <p class="text-h6 grey--text text--darken-1 mb-2">
          {{ translations.advice_no_clients }}
        </p>
        <p class="text-body-2 grey--text">
          {{ translations.advice_no_clients_subtitle }}
        </p>
      </v-col>
    </v-row>

    <!-- Fixed Footer Spacer -->
    <div class="footer-spacer" style="height: 60px;"></div>
  </v-container>
</template>

<script>
/**
 * ClientsContainer Component
 *
 * Container component for displaying a grid of client cards with infinite scrolling,
 * search functionality, and responsive layout. Handles client data fetching and
 * state management.
 *
 * @component
 * @example
 * <clients-container />
 */

// ===== IMPORTS =====
import { mapActions, mapState, mapMutations, mapGetters } from "vuex";
import Client from "@/components/home/items/Client.vue";
import axios from "axios";

export default {
  name: "ClientsContainer",

  components: {
    Client,
  },

  // ===== REACTIVE STATE =====
  data() {
    return {
      /**
       * Array of client objects to display
       * @type {Array<Object>}
       */
      clients: [],

      /**
       * Loading state for initial data fetch
       * @type {boolean}
       */
      loading: true,

      /**
       * Current pagination page
       * @type {number}
       */
      page: 0,

      /**
       * Axios cancel token source for request cancellation
       * @type {Object|null}
       */
      request: null,

      /**
       * Loading state for infinite scroll
       * @type {boolean}
       */
      busyGettingData: false,

      /**
       * Flag indicating if more data is available
       * @type {boolean}
       */
      isMoreData: true,

      /**
       * Reference to scroll event listener
       * @type {Function|null}
       */
      scrollListener: null,
    };
  },

  // ===== COMPOSABLES & STORES =====
  computed: {
    ...mapGetters("session", ["currentTranslations"]),
    ...mapState("user", ["search", "fetchClients"]),
  },

  // ===== COMPUTED PROPERTIES =====
  computed: {
    ...mapGetters("session", ["currentTranslations"]),
    ...mapState("user", ["search", "fetchClients"]),

    /**
     * Current translations from store
     * @returns {Object} Translation key-value pairs
     */
    translations() {
      return this.currentTranslations;
    },

    /**
     * Number of skeleton loaders based on viewport
     * @returns {number} Number of skeleton items to display
     */
    skeletonCount() {
      const breakpoint = this.$vuetify.breakpoint.name;
      const counts = {
        xs: 4,
        sm: 6,
        md: 8,
        lg: 9,
        xl: 12,
      };
      return counts[breakpoint] || 6;
    },

    /**
     * Column span for skeleton loaders
     * @returns {number} Column span value
     */
    skeletonCols() {
      const breakpoint = this.$vuetify.breakpoint.name;
      return breakpoint === "xs" ? 12 : 6;
    },

    /**
     * Responsive grid column configuration
     * @returns {number} Column span value
     */
    gridCols() {
      const breakpoint = this.$vuetify.breakpoint.name;
      const cols = {
        xs: 12, // Mobile: 1 column
        sm: 6, // Small tablet: 2 columns
        md: 4, // Tablet: 3 columns
        lg: 4, // Desktop: 3 columns
        xl: 3, // Large desktop: 4 columns
      };
      return cols[breakpoint] || 12;
    },
  },

  // ===== LIFECYCLE HOOKS =====
  async mounted() {
    this.SET_LOADER(true);
    try {
      await this.initializeComponent();
    } catch (error) {
      console.error("Component initialization error:", error);
      this.showErrorAlert();
    } finally {
      this.SET_LOADER(false);
    }
  },

  beforeDestroy() {
    this.cleanupComponent();
  },

  // ===== WATCHERS =====
  watch: {
    /**
     * Watches for search term changes and refetches data
     * @param {string} newSearch - New search term
     */
    async search(newSearch) {
      await this.fetchData();
    },

    /**
     * Watches for fetchClients flag to trigger data refresh
     * @param {boolean} value - Flag indicating if clients should be refetched
     */
    async fetchClients(value) {
      if (value) {
        this.FETCH_CLIENTS(false);
        await this.fetchData();
      }
    },
  },

  // ===== METHODS =====
  methods: {
    ...mapActions("user", ["getClients"]),
    ...mapMutations("user", ["FETCH_CLIENTS"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapMutations(["SET_LOADER"]),

    // ===== INITIALIZATION METHODS =====
    /**
     * Initializes component with scroll listener and initial data fetch
     */
    async initializeComponent() {
      this.scrollListener = this.throttle(this.handleScroll, 200);
      window.addEventListener("scroll", this.scrollListener);
      await this.fetchData();
    },

    /**
     * Cleans up component resources and event listeners
     */
    cleanupComponent() {
      if (this.scrollListener) {
        window.removeEventListener("scroll", this.scrollListener);
      }
      if (this.request) {
        this.request.cancel("Component destroyed");
      }
    },

    // ===== API METHODS =====
    /**
     * Creates axios cancel token for request cancellation
     * @returns {Object} Cancel token source
     */
    createCancelToken() {
      const source = axios.CancelToken.source();
      this.request = source;
      return source;
    },

    /**
     * Fetches initial client data with search and pagination
     */
    async fetchData() {
      const searchText = this.search || "";

      // Reset state for new fetch
      this.clients = [];
      this.page = 0;
      this.isMoreData = true;
      this.loading = true;

      this.scrollTop();

      try {
        // Cancel previous request if exists
        if (this.request) {
          this.request.cancel("Operation canceled");
        }

        const response = await this.getClients({
          page: this.page,
          search: searchText,
          source: this.createCancelToken(),
        });

        if (response && response.clients) {
          this.clients = response.clients;
          this.isMoreData = response.clients.length > 0;
          this.page++;
        }
      } catch (error) {
        console.error("Data fetch error:", error);
        if (!axios.isCancel(error)) {
          this.showErrorAlert();
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Loads more data for infinite scrolling
     */
    async loadMoreData() {
      if (this.busyGettingData || !this.isMoreData) return;

      this.busyGettingData = true;

      try {
        const response = await this.getClients({
          page: this.page,
          search: this.search || "",
          source: this.createCancelToken(),
        });

        if (response && response.clients.length > 0) {
          this.clients = [...this.clients, ...response.clients];
          this.page++;
        } else {
          this.isMoreData = false;
        }
      } catch (error) {
        console.error("Load more data error:", error);
        if (!axios.isCancel(error)) {
          this.showErrorAlert();
        }
      } finally {
        this.busyGettingData = false;
      }
    },

    // ===== UTILITY FUNCTIONS =====
    /**
     * Throttle function to limit scroll event frequency
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in milliseconds
     * @returns {Function} Throttled function
     */
    throttle(func, limit) {
      let inThrottle;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    },

    /**
     * Handles scroll events for infinite loading
     */
    handleScroll() {
      if (this.busyGettingData || !this.isMoreData) return;

      const scrollPosition = window.innerHeight + window.pageYOffset;
      const documentHeight = document.documentElement.offsetHeight;
      const threshold = 100;

      if (scrollPosition >= documentHeight - threshold) {
        this.loadMoreData();
      }
    },

    /**
     * Scrolls to top of page
     */
    scrollTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // ===== EVENT HANDLERS =====
    /**
     * Shows error alert to user
     */
    showErrorAlert() {
      this.SET_ALERT_TYPE({
        visible: true,
        type: "error",
        text: this.translations.advice_error,
        timeout: 4000,
      });
    },
  },
};
</script>

<style scoped>
.fill-height {
  min-height: calc(100vh - 120px);
}

.footer-spacer {
  flex-shrink: 0;
}

/* Performance Optimizations */
.v-card {
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .pa-1 {
    padding: 4px !important;
  }
}

/* Accessibility Improvements */
@media (prefers-reduced-motion: reduce) {
  .v-card {
    transition: none;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .footer-spacer {
    background-color: transparent;
  }
}
</style>
